import { API_CONFIG, API_ENDPOINTS, ML_API_ENDPOINTS, STORAGE_KEYS } from '../helpers/constants';

/** How the backend produced the suggestions (BUSINESS_RULE_GENERATION_HARDENING_TDD §6). The
 * server derives it from channel_id + saved rules; the client never requests a mode.
 * 'create' = from description/tags only, 'create+context' = also grounded in the channel's
 * connected apps, 'refine' = improvement of the channel's saved rules. */
export type BusinessRulesGenerationMode = 'create' | 'create+context' | 'refine';

export interface BusinessRulesSuggestion {
  rules: string[];
  validation_result?: BusinessRulesValidationResult;
  /** Echo of the mode the server actually used. Absent when the backend predates P2 or has the
   * BUSINESS_RULE_GEN_CONTEXT flag off — callers treat that as "legacy, no refine support". */
  mode?: BusinessRulesGenerationMode;
}

export interface GenerateBusinessRulesOptions {
  /** Sent only when non-empty; lets the backend load connected apps / saved rules for this
   * channel (tenant-checked server side). */
  channelId?: string;
}

export interface GenerateBusinessRulesRequest {
  email_id: string;
  workspace_name: string;
  categories: string[];
  channel_description: string;
  /** Subtags selected by admin (e.g. ["payroll", "leave request"]) — optional, matches the
   * backend's BusinessRulesRequest.tags field. */
  tags?: string[];
  /** Channel platform type sent to the ML backend. Omit for email (backend default).
   * Non-email values: 'google_chat' | 'slack' | 'teams' | 'regular' | 'whatsapp'. */
  channel_type?: string;
  channel_id?: string;
}

export interface GenerateBusinessRulesResponse {
  status: string;
  message?: string;
  business_rules?: string[];
  channel_routing_md?: string;
  validation_result?: BusinessRulesValidationResult;
  mode?: BusinessRulesGenerationMode;
}

const GENERATION_MODES: readonly string[] = ['create', 'create+context', 'refine'];

export type BusinessRulesValidationStatus = 'errors' | 'warnings' | 'clean';

export interface BusinessRulesValidationIssue {
  check?: string;
  rule_excerpt?: string;
  message?: string;
  severity?: 'error' | 'warning' | string;
  cta_url?: string;
  cta_label?: string;
}

export interface BusinessRulesValidationResult {
  status: BusinessRulesValidationStatus;
  errors: BusinessRulesValidationIssue[];
  warnings: BusinessRulesValidationIssue[];
  validated_at?: string;
}

function normalizeValidationIssue(
  raw: string | BusinessRulesValidationIssue,
  severity: 'error' | 'warning'
): BusinessRulesValidationIssue {
  if (typeof raw === 'string') {
    return {
      check: severity === 'error' ? 'Validation error' : 'Validation warning',
      rule_excerpt: '',
      message: raw,
      severity,
    };
  }

  return {
    check: raw.check || (severity === 'error' ? 'Validation error' : 'Validation warning'),
    rule_excerpt: raw.rule_excerpt || '',
    message: raw.message || raw.check || 'Validation issue found.',
    severity: raw.severity || severity,
    ...(raw.cta_url ? { cta_url: raw.cta_url } : {}),
    ...(raw.cta_label ? { cta_label: raw.cta_label } : {}),
  };
}

function normalizeValidationResult(raw: unknown): BusinessRulesValidationResult {
  const payload = (raw ?? {}) as {
    status?: BusinessRulesValidationStatus;
    errors?: Array<string | BusinessRulesValidationIssue>;
    warnings?: Array<string | BusinessRulesValidationIssue>;
    validated_at?: string;
  };

  const errors = Array.isArray(payload.errors)
    ? payload.errors.map((item) => normalizeValidationIssue(item, 'error'))
    : [];
  const warnings = Array.isArray(payload.warnings)
    ? payload.warnings.map((item) => normalizeValidationIssue(item, 'warning'))
    : [];

  const status: BusinessRulesValidationStatus =
    payload.status === 'errors' || payload.status === 'warnings' || payload.status === 'clean'
      ? payload.status
      : errors.length > 0
        ? 'errors'
        : warnings.length > 0
          ? 'warnings'
          : 'clean';

  return {
    status,
    errors,
    warnings,
    ...(payload.validated_at ? { validated_at: payload.validated_at } : {}),
  };
}

function getBearerToken(): string {
  return (
    localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN) ||
    localStorage.getItem(STORAGE_KEYS.AUTH_TOKEN) ||
    ''
  );
}

async function parseApiErrorMessage(response: Response): Promise<string> {
  const errorData = await response.json().catch(() => ({}));

  if (typeof errorData.detail === 'string') {
    return errorData.detail;
  }
  if (Array.isArray(errorData.detail) && errorData.detail.length > 0) {
    const firstError = errorData.detail[0];
    if (firstError?.msg) return firstError.msg;
    if (typeof firstError === 'string') return firstError;
  }
  if (errorData.message) return errorData.message;
  if (errorData.error) return errorData.error;

  return `Business rules generation failed: ${response.status}`;
}

/**
 * Call orchestrator to generate business rule suggestions.
 * Sequential after description — channelDescription is required.
 */
export async function generateBusinessRules(
  emailId: string,
  workspaceName: string,
  categories: string[],
  channelDescription: string,
  tags?: string[],
  channelType?: string,
  options?: GenerateBusinessRulesOptions,
): Promise<BusinessRulesSuggestion> {
  const token = getBearerToken();
  if (!token) {
    throw new Error('Authentication required. Please sign in again.');
  }

  const channelId = options?.channelId?.trim();
  const payload: GenerateBusinessRulesRequest = {
    email_id: emailId,
    workspace_name: workspaceName,
    categories,
    channel_description: channelDescription,
    ...(tags && tags.length > 0 ? { tags } : {}),
    ...(channelType ? { channel_type: channelType } : {}),
    ...(channelId ? { channel_id: channelId } : {}),
  };

  const response = await fetch(
    `${API_CONFIG.ML_API_BASE_URL}${ML_API_ENDPOINTS.GENERATE_BUSINESS_RULES}`,
    {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    }
  );

  if (!response.ok) {
    throw new Error(await parseApiErrorMessage(response));
  }

  const data = (await response.json()) as GenerateBusinessRulesResponse;

  if (data.status !== 'success') {
    throw new Error(data.message || 'Business rules generation failed');
  }

  const rules = Array.isArray(data.business_rules)
    ? data.business_rules
    : (typeof data.channel_routing_md === 'string' && data.channel_routing_md.trim()
      ? [data.channel_routing_md]
      : []);
  return {
    rules,
    validation_result: data.validation_result
      ? normalizeValidationResult(data.validation_result)
      : undefined,
    ...(data.mode && GENERATION_MODES.includes(data.mode) ? { mode: data.mode } : {}),
  };
}

/**
 * Save channel business rules via base_shay channel update API.
 * Triggers background generation of channel_routing_md automatically.
 */
export async function saveChannelBusinessRules(
  channelId: string,
  businessRules: string,
  options?: { forceSave?: boolean; validationResult?: BusinessRulesValidationResult | null },
): Promise<void> {
  const token = getBearerToken();
  if (!token) {
    throw new Error('Authentication required. Please sign in again.');
  }

  const response = await fetch(
    `${API_CONFIG.API_BASE_URL}${API_ENDPOINTS.CHANNELS.UPDATE(channelId)}`,
    {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        channel_business_rules: businessRules,
        ...(options?.forceSave ? { force_save: true } : {}),
        ...(options?.validationResult ? { validation_result: options.validationResult } : {}),
      }),
    }
  );

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.detail || `Failed to save business rules: ${response.status}`);
  }
}

export async function validateChannelBusinessRules(
  channelId: string,
  rulesText: string,
  options?: { emailId?: string },
): Promise<BusinessRulesValidationResult> {
  const token = getBearerToken();
  if (!token) {
    throw new Error('Authentication required. Please sign in again.');
  }

  if (!channelId && !options?.emailId) {
    throw new Error('Channel or email is required for validation.');
  }

  const response = await fetch(
    `${API_CONFIG.ML_API_BASE_URL}${ML_API_ENDPOINTS.VALIDATE_CHANNEL_RULES}`,
    {
      method: 'POST',
      headers: {
        accept: 'application/json',
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        ...(channelId ? { channel_id: channelId } : {}),
        ...(options?.emailId ? { email_id: options.emailId } : {}),
        rules_text: rulesText,
      }),
    }
  );

  if (!response.ok) {
    throw new Error(await parseApiErrorMessage(response));
  }

  const data = await response.json();
  return normalizeValidationResult(data);
}
